import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { Control, Controller, FieldPath, FieldValues, useForm, useWatch } from 'react-hook-form';
import cn from 'clsx';
import reviewStyles from './style.module.scss';
import { BasicDropDownController } from '@components-clean/core/basic-dropdown';
import { DatePickerController } from '@components-clean/core/date-picker';
import { getUTCDate } from '@utils-clean/date-time-helpers';
import { useTranslation } from '@hooks/index';
import { RiskService } from '@utils/risk.service';

export type RiskStatusClosedDateFieldsType = {
    riskStatus: { id: string; text: string } | null;
    closedDate: Date | null;
};

interface RiskStatusClosedDateSelectorProps {
    value?: RiskStatusClosedDateFieldsType;
    onChange?: ((value: RiskStatusClosedDateFieldsType) => void) | undefined;
    disabled?: boolean;
    onRiskStatusChange?: (statusId: string | undefined) => void;
    'data-testid': string;
}

const RiskStatusClosedDateSelector = ({
    value,
    onChange,
    disabled,
    onRiskStatusChange,
    'data-testid': dataTestId,
}: RiskStatusClosedDateSelectorProps) => {
    const columnRef = useRef<HTMLDivElement>(null);
    const { t } = useTranslation('ram', { keyPrefix: 'risks.dialogs.addRisk' });

    const riskStatuses: { id: string; text: string }[] = Object.assign(
        [],
        RiskService.riskStatusList.filter((s: { id: string }) => s.id !== 'DRAFT'),
    );

    useLayoutEffect(() => {
        columnRef.current?.setAttribute('data-testid', dataTestId);
        return () => {
            columnRef.current = null;
        };
    }, [dataTestId]);

    const { control, resetField } = useForm<RiskStatusClosedDateFieldsType>({
        mode: 'all',
        defaultValues: {
            riskStatus: value ? value.riskStatus : null,
            closedDate: value ? value.closedDate : null,
        },
    });

    const riskStatus = useWatch({ control, name: 'riskStatus' });
    const closedDate = useWatch({ control, name: 'closedDate' });

    useEffect(() => {
        if (!value) return;

        if (riskStatus?.id !== value.riskStatus?.id) {
            resetField('riskStatus', { defaultValue: value.riskStatus });
        }
        if (closedDate !== value.closedDate) {
            resetField('closedDate', { defaultValue: value.closedDate });
        }
    }, [value]);

    const closedStatusSelected = riskStatus?.id === 'CLOSED';

    return (
        <form id="bulk-update-risk-status-closed-date-fields" className="k-w-full k-h-full">
            <div className={cn('k-pt-1', reviewStyles.riskConfigurationContainer)} ref={columnRef}>
                <div className={cn('k-shrink-0 k-d-flex k-justify-content-left k-ml-2 k-mr-2')}>
                    <div className="k-d-flex k-w-full k-gap-2">
                        <div className={cn('!k-basis-1/2', reviewStyles.minWidthFix, reviewStyles.dropdownFix)}>
                            <BasicDropDownController
                                control={control}
                                name="riskStatus"
                                label={t('fields.status')}
                                data-testid="risk-field-status"
                                data={riskStatuses}
                                textField="text"
                                dataItemKey="id"
                                fillMode="solid"
                                filterable
                                required
                                allowClear={false}
                                readOnly={disabled}
                                handleInputChange={(val) => {
                                    onRiskStatusChange?.(val.value?.id);
                                    if (!disabled && onChange) {
                                        const nextClosedDate =
                                            val.value?.id === 'CLOSED' ? (closedDate ?? getUTCDate()) : null;
                                        resetField('closedDate', { defaultValue: nextClosedDate });
                                        onChange({
                                            riskStatus: val.value,
                                            closedDate: nextClosedDate,
                                        });
                                    }
                                }}
                            />
                        </div>
                        <div className={cn('!k-basis-1/2', reviewStyles.minWidthFix)}>
                            <DatePickerController
                                control={control}
                                name="closedDate"
                                label={t('fields.closedDate')}
                                data-testid="risk-field-closed-date"
                                fillMode="solid"
                                required={closedStatusSelected}
                                readonly={disabled || !closedStatusSelected}
                                handleInputChange={(val) => {
                                    if (!disabled && onChange) {
                                        onChange({
                                            riskStatus,
                                            closedDate: val.value,
                                        });
                                    }
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </form>
    );
};

interface RiskStatusClosedDateFieldControllerProps<TData, TControl extends FieldValues> {
    control: Control<TControl>;
    name: FieldPath<TControl>;
    validateRules?: Record<string, (value: TData | null) => string | boolean | undefined>;
    handleInputChange?: (value: RiskStatusClosedDateFieldsType) => void;
    onRiskStatusChange?: (statusId: string | undefined) => void;
    disabled?: boolean;
    required?: boolean;
    'data-testid': string;
}

const RiskStatusClosedDateFieldController = <TData, TControl extends FieldValues>({
    control,
    name,
    disabled,
    'data-testid': dataTestId,
    required = false,
    validateRules,
    handleInputChange,
    onRiskStatusChange,
}: RiskStatusClosedDateFieldControllerProps<TData, TControl>) => {
    const { t } = useTranslation();

    return (
        <Controller
            control={control}
            name={name}
            rules={{
                required: {
                    value: required,
                    message: t('ra:requiredFieldError'),
                },
                validate: {
                    ...validateRules,
                },
            }}
            render={({ field: { onChange, value } }) => {
                return (
                    <RiskStatusClosedDateSelector
                        value={value}
                        onChange={(val) => {
                            onChange(val);
                            if (handleInputChange) handleInputChange(val);
                        }}
                        disabled={disabled}
                        onRiskStatusChange={onRiskStatusChange}
                        data-testid={dataTestId}
                    />
                );
            }}
        />
    );
};

export { RiskStatusClosedDateFieldController };
